import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, Sparkles, CheckCircle2, Calendar as CalendarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarEvent } from '@/types/calendar';
import { format, subMinutes } from 'date-fns';
import { toast } from 'sonner';

export interface PrepTimeSlot {
  meetingId: string;
  meetingTitle: string;
  meetingStart: Date;
  prepStart: Date;
  prepEnd: Date;
  prepDuration: number;
}

interface MeetingPrepSchedulerProps {
  events: CalendarEvent[];
  onSchedulePrep: (prepSlots: PrepTimeSlot[]) => void;
}

export function MeetingPrepScheduler({ events, onSchedulePrep }: MeetingPrepSchedulerProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [prepSlots, setPrepSlots] = useState<PrepTimeSlot[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Get today's meetings
  const getTodaysMeetings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return events.filter(event => {
      const eventStart = new Date(event.startTime || event.start);
      return (
        eventStart >= today &&
        eventStart < tomorrow &&
        (event.eventType === 'meeting' || event.title.toLowerCase().includes('meeting'))
      );
    });
  };

  // Calculate prep time based on meeting duration
  const calculatePrepDuration = (meetingDurationMinutes: number): number => {
    if (meetingDurationMinutes <= 30) return 15; // 15 min prep for short meetings
    if (meetingDurationMinutes <= 60) return 30; // 30 min prep for 1hr meetings
    return 30; // 30 min prep for longer meetings (capped)
  };

  // Check if time slot conflicts with existing events
  const hasConflict = (start: Date, end: Date): boolean => {
    return events.some(event => {
      const eventStart = new Date(event.startTime || event.start);
      const eventEnd = new Date(event.endTime || event.end);
      return (
        (start >= eventStart && start < eventEnd) ||
        (end > eventStart && end <= eventEnd) ||
        (start <= eventStart && end >= eventEnd)
      );
    });
  };

  const analyzeMeetings = () => {
    setIsAnalyzing(true);
    
    // Simulate AI analysis with animation
    setTimeout(() => {
      const meetings = getTodaysMeetings();
      console.log('📅 Analyzing meetings:', meetings.map(m => ({ id: m.id, title: m.title, category: m.category, color: m.color })));
      const calculatedPrepSlots: PrepTimeSlot[] = [];

      meetings.forEach(meeting => {
        const meetingStart = new Date(meeting.startTime || meeting.start);
        const meetingEnd = new Date(meeting.endTime || meeting.end);
        const meetingDuration = (meetingEnd.getTime() - meetingStart.getTime()) / (1000 * 60);
        
        const prepDuration = calculatePrepDuration(meetingDuration);
        const prepStart = subMinutes(meetingStart, prepDuration);
        const prepEnd = new Date(meetingStart);

        // Check for conflicts
        if (!hasConflict(prepStart, prepEnd)) {
          calculatedPrepSlots.push({
            meetingId: meeting.id,
            meetingTitle: meeting.title,
            meetingStart,
            prepStart,
            prepEnd,
            prepDuration
          });
        }
      });

      console.log('📅 Calculated prep slots:', calculatedPrepSlots.length);
      setPrepSlots(calculatedPrepSlots);
      setIsAnalyzing(false);
      setShowDialog(true);
    }, 1500);
  };

  const handleConfirmSchedule = () => {
    onSchedulePrep(prepSlots);
    setShowDialog(false);
    toast.success(`Scheduled ${prepSlots.length} prep sessions!`, {
      description: 'Prep time blocks added before your meetings'
    });
  };

  const todaysMeetings = getTodaysMeetings();

  if (todaysMeetings.length === 0) {
    return null;
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-4 rounded-xl border-primary/20 bg-gradient-to-r from-primary/5 to-purple-500/5"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <motion.div
              className="p-2 glass rounded-lg"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3
              }}
            >
              <Sparkles className="w-5 h-5 text-primary" />
            </motion.div>
            <div>
              <div className="font-semibold flex items-center gap-2">
                Meeting Prep Assistant
                <Badge variant="secondary" className="text-xs">
                  AI-Powered
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {todaysMeetings.length} {todaysMeetings.length === 1 ? 'meeting' : 'meetings'} today • Auto-schedule prep time
              </div>
            </div>
          </div>

          <Button
            onClick={analyzeMeetings}
            disabled={isAnalyzing}
            className="gap-2 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
          >
            {isAnalyzing ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-4 h-4" />
                </motion.div>
                Analyzing...
              </>
            ) : (
              <>
                <CalendarIcon className="w-4 h-4" />
                Prepare Today's Meetings
              </>
            )}
          </Button>
        </div>
      </motion.div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 glass rounded-lg">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              Meeting Prep Schedule
            </DialogTitle>
            <DialogDescription>
              Review AI-suggested prep time for your meetings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            <AnimatePresence>
              {prepSlots.map((slot, index) => (
                <motion.div
                  key={slot.meetingId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-4 rounded-lg border-l-4 border-primary"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 glass rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold mb-1">{slot.meetingTitle}</div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Prep: {format(slot.prepStart, 'h:mm a')} - {format(slot.prepEnd, 'h:mm a')}</span>
                          <Badge variant="secondary" className="text-xs">
                            {slot.prepDuration} min
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4" />
                          <span>Meeting: {format(slot.meetingStart, 'h:mm a')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {prepSlots.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No available time slots found</p>
                <p className="text-sm mt-1">Your schedule is fully booked</p>
              </div>
            )}
          </div>

          {prepSlots.length > 0 && (
            <div className="glass-card p-3 rounded-lg bg-primary/5 border-primary/20">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Smart Scheduling:</span> AI analyzed your calendar and found optimal prep times that won't conflict with existing events.
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSchedule}
              disabled={prepSlots.length === 0}
              className="gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Schedule {prepSlots.length} Prep {prepSlots.length === 1 ? 'Session' : 'Sessions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
